import { useState } from "react";
import CssBaseline from '@mui/material/CssBaseline';
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import {
  Autocomplete,
  Avatar,
  Box,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip
} from '@mui/material';
import { setSelectedLink } from '../store/action/globalActions';
// ICONS
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WavesIcon from '@mui/icons-material/Waves';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
// import FeedIcon from '@mui/icons-material/FeedOutlined';
// import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
// import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
// import AddchartOutlinedIcon from '@mui/icons-material/AddchartOutlined';

const drawerWidth = 240;
const miniDrawerWidth = 80;
const headerHeight = 64;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: miniDrawerWidth,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  minHeight: headerHeight,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  marginLeft: open ? drawerWidth : miniDrawerWidth,
  width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${miniDrawerWidth}px)`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  padding: 0
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

const mainNavItems = [
  {
    label: "Overview",
    to: "/",
    icon: <calcite-icon icon="home" />
  },
  {
    label: "Data Hub",
    to: "/data-hub",
    icon: <calcite-icon icon="data" /> //alternative: collection
  },
  {
    label: "Grades/DEM",
    to: "/grades-dem",
    icon: <calcite-icon icon="altitude" /> //alternative: contour
  },
  {
    label: "Curvature",
    to: "/curvature-geometry",
    icon: <WavesIcon /> //alternative: curvature
  },
  {
    label: "Safety Screening",
    to: "/safety-screening",
    icon: <HealthAndSafetyIcon /> //alternative: curvature
  },
  {
    label: "Investment Options",
    to: "/investment-options",
    icon: <TrendingUpIcon />
  },
  {
    label: "Scenario Planning",
    to: "/scenario-planning",
    icon: <AccountTreeIcon /> //alternative: curvature
  },
  {
    label: "Program Execution",
    to: "/program-execution",
    icon: <RocketLaunchIcon /> //alternative: curvature
  },


];

const secondaryNavItems = [
  { label: "Settings", to: "/settings", icon: <calcite-icon icon="gear" /> },
  { label: "Help", to: "/help", icon: <calcite-icon icon="question" /> },
];

export default function LeftDrawer() {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState(mainNavItems[0].label);
  const location = useLocation();
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const linkOptions = [
    { label: 'Gregory Highway', id: 1 },
    { label: 'Test Highway', id: 2 },
  ];
  const selectedLink = useSelector((state: any) => state.global.selectedLink);
  const dispatch = useDispatch();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ background: "#fff" }}>
        <Toolbar disableGutters sx={{ minHeight: `${headerHeight}px !important`, px: 2 }}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={() => setOpen(!open)}
            edge="start"
            sx={[
              {
                marginRight: 2,
              }
            ]}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" color="primary">
            {selectedLabel}
          </Typography>
          <Autocomplete
            sx={{ ml: 4, width: 250, py: 2.2, '& .MuiInputBase-input': { fontSize: '14px' } }}
            key={selectedLink}
            value={selectedLink}
            onChange={(event, newValue) => {
              dispatch(setSelectedLink(newValue?.label));
            }}
            slotProps={{ paper: { sx: { '& .MuiAutocomplete-option': { fontSize: '14px' } } } }}
            disablePortal
            options={linkOptions}
            renderInput={(params) => <TextField {...params} label="IFR Corridor" sx={{ '& .MuiInputLabel-root': { fontSize: '14px' } }} />}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Avatar
            sx={[
              {
                width: 32,
                height: 32,
                color: 'primary.contrastText',
                fontSize: 16,
              },
              open
                ? { mr: 2 }
                : { mr: 0 },
            ]}
          >
            <Typography>AD</Typography>
          </Avatar>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open} sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: 'primary.main',
          overflow: 'visible',
        },
      }}>
        <DrawerHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "flex-start" : "center",
              width: "100%",
              minHeight: headerHeight,
              px: 1,
              overflow: "hidden"
            }}>
            <img src="/IFRMockupLogoIcon.png"
              alt="IFR Logo"
              style={{
                width: open ? 32 : 35,
                height: open ? 32 : 35,
                display: "block"
              }} />

            {open && (
              <Typography variant="subtitle2" sx={{ color: "primary.contrastText", ml: 1, lineHeight: 1.2, fontWeight: 600 }} >
                IFR Digital <br /> Route Assessment<br /> Portal
              </Typography>
            )}
          </Box>
        </DrawerHeader>

        <Divider />
        {/* <Toolbar /> */}

        <List>
          {mainNavItems.map(({ label, to, icon }) => {
            const isDisabled = ["/safety-screening", "/scenario-planning", "/investment-options", "/program-execution"].includes(to);
            return (
              <ListItem
                component={isDisabled ? 'div' : RouterLink}
                to={isDisabled ? '' : to}
                key={to}
                disablePadding
                sx={{
                  display: 'block',
                  px: 1,
                  py: 0.5,
                  // cursor: isDisabled ? 'not-allowed' : 'pointer',
                  // display: 'block',
                  // color: 'primary.contrastText',
                  // "&.Mui-selected, &.Mui-selected:hover": {
                  //   backgroundColor: "#e0f2f1", // MUI teal lighten-5, adjust as you like
                  //   color: "#00695c",            // MUI teal darken-3, adjust as you like
                  //   fontWeight: "bold",
                  // },
                }}
              >
                <ListItemButton
                  selected={location.pathname === to}
                  disabled={isDisabled}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    mx: '10px',
                    borderRadius: "5px",
                    color: 'primary.contrastText',
                    transition: 'background-color 0.2s ease',
                    "&:hover": {
                      bgcolor: '#063C9B !important',
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: '#063C9B !important',
                    },
                    justifyContent: open ? 'initial' : 'center',
                  }}
                  onClick={() => setSelectedLabel(label)}
                >
                  <Tooltip title={label}>
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: 'center',
                          color: 'primary.contrastText',
                        },
                        open
                          ? {
                            mr: 3,
                          }
                          : {
                            mr: 'auto',
                          },
                      ]}
                    >
                      {icon && icon}
                    </ListItemIcon>
                  </Tooltip>

                  <ListItemText
                    primary={label}
                    sx={[
                      open
                        ? {
                          opacity: 1,
                        }
                        : {
                          opacity: 0,
                        },
                    ]}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
        <Divider />
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <List>
          {secondaryNavItems.map(({ label, to, icon }) => (
            <ListItem
              component={RouterLink}
              key={to}
              to={to}
              disablePadding
              sx={{
                color: 'primary.contrastText',
                display: 'block',
                px: 1,
                py: 0.5,
              }}
            >
              <ListItemButton
                selected={location.pathname === to}
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                    mx: '10px',
                    // my: 0.5,
                    borderRadius: "5px",
                    color: 'primary.contrastText',
                    transition: 'background-color 0.2s ease',
                    "&:hover": {
                      bgcolor: '#063C9B !important',
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: '#063C9B !important',
                    },
                  },
                  open
                    ? {
                      justifyContent: 'initial',
                    }
                    : {
                      justifyContent: 'center',
                    },
                ]}
                onClick={() => setSelectedLabel(label)}
              >
                <Tooltip title={label}>
                  <ListItemIcon
                    sx={[
                      {
                        minWidth: 0,
                        justifyContent: 'center',
                        color: 'primary.contrastText',
                      },
                      open
                        ? {
                          mr: 3,
                        }
                        : {
                          mr: 'auto',
                        },
                    ]}
                  >
                    {icon && icon}
                  </ListItemIcon>
                </Tooltip>

                <ListItemText
                  primary={label}
                  sx={[
                    open
                      ? {
                        opacity: 1,
                      }
                      : {
                        opacity: 0,
                      },
                  ]}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ borderColor: '#42516a' }} />
        {/* alternative divider color: 22314a */}

        <List>
          <ListItem
            disablePadding
            sx={{
              display: 'block',
              color: 'primary.contrastText',
              px: 1,
              py: 0.5,
            }}
          >
            <ListItemButton
              sx={[
                {
                  pl: 2.5,
                  color: 'primary.contrastText',
                  px: 2.5,
                  mx: '10px',
                  borderRadius: "5px",
                  transition: 'background-color 0.2s ease',
                  "&:hover": {
                    bgcolor: '#063C9B !important',
                  },
                  "&.Mui-selected, &.Mui-selected:hover": {
                    bgcolor: '#063C9B !important',
                  },
                },
                open
                  ? { justifyContent: 'initial' }
                  : { justifyContent: 'center' },
              ]}
            >
              {/* Avatar: Always visible */}
              <Avatar
                sx={[
                  {
                    width: 32,
                    height: 32,
                    color: 'primary.contrastText',
                    fontSize: 16,
                  },
                  open
                    ? { mr: 2 }
                    : { mr: 0 },
                ]}
              >
                <Typography>AD</Typography>
              </Avatar>

              {/* Details: Hidden when drawer is closed */}
              <Box
                sx={[
                  {
                    transition: 'opacity 0.3s',
                    ml: 1,
                    minWidth: 0, // for flexbox shrinking
                  },
                  open
                    ? { opacity: 1, width: 'auto', display: 'block' }
                    : { opacity: 0, width: 0, display: 'none' },
                ]}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Admin User
                </Typography>

              </Box>

              <Box sx={{ flexGrow: 1 }} />

              {/* Chevron: Only when open */}
              {open && <KeyboardArrowDownIcon />}
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Box component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // background: 'pink',
          overflowY: 'auto',
          mt: '24.33px'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}